import React, { useState, useEffect } from 'react';
import { TabletFilterContainer } from './shared/TabletFilterContainer';
import { TabletFilterField } from './shared/TabletFilterField';
import { TabletFilterSection } from './shared/TabletFilterSection';
import { FilterOption, SelectedItem } from './shared/TabletFilterOptionsList';
import styles from './shared/TabletFilterContainer.module.scss';

export interface ActFilterData {
  number?: string;
  statuses?: string[];
  create_date_range?: {
    start: string;
    end: string;
  };
}

interface ActFilterProps {
  initialData?: ActFilterData;
  onApply: (data: ActFilterData) => void;
  onReset: () => void;
  onChange?: (data: ActFilterData) => void;
}

const statusOptions: FilterOption[] = [
  { value: 'signed_by_customer', label: 'Подписан заказчиком' },
  { value: 'signed_by_worker', label: 'Подписан исполнителем' },
];

const getStatusLabel = (statusId: string): string => {
  const option = statusOptions.find((opt) => opt.value === statusId);
  return option?.label || statusId;
};

export const TabletActFilter: React.FC<ActFilterProps> = ({ initialData, onChange }) => {
  const [actNumber, setActNumber] = useState<string>(initialData?.number || '');
  const [selectedStatuses, setSelectedStatuses] = useState<SelectedItem[]>(
    initialData?.statuses?.map((status) => ({
      id: status,
      name: getStatusLabel(status),
    })) || [],
  );
  const [actDateStart, setActDateStart] = useState<Date | null>(
    initialData?.create_date_range?.start
      ? new Date(initialData.create_date_range.start)
      : null,
  );
  const [actDateEnd, setActDateEnd] = useState<Date | null>(
    initialData?.create_date_range?.end &&
      initialData.create_date_range.end !== initialData.create_date_range.start
      ? new Date(initialData.create_date_range.end)
      : initialData?.create_date_range?.start
      ? new Date(initialData.create_date_range.start)
      : null,
  );

  useEffect(() => {
    setActNumber(initialData?.number || '');
    setSelectedStatuses(
      initialData?.statuses?.map((status) => ({
        id: status,
        name: getStatusLabel(status),
      })) || [],
    );
    setActDateStart(
      initialData?.create_date_range?.start
        ? new Date(initialData.create_date_range.start)
        : null,
    );
    setActDateEnd(
      initialData?.create_date_range?.end &&
        initialData.create_date_range.end !== initialData.create_date_range.start
        ? new Date(initialData.create_date_range.end)
        : initialData?.create_date_range?.start
        ? new Date(initialData.create_date_range.start)
        : null,
    );
  }, [initialData]);

  const updateParentData = (
    number: string,
    statuses: SelectedItem[],
    dateStart: Date | null,
    dateEnd: Date | null,
  ) => {
    if (onChange) {
      const filterData: ActFilterData = {};

      if (number) {
        filterData.number = number;
      }

      if (statuses.length > 0) {
        filterData.statuses = statuses.map((item) => item.id);
      }

      if (dateStart || dateEnd) {
        const startStr = dateStart
          ? formatDateForAPI(dateStart)
          : dateEnd
          ? formatDateForAPI(dateEnd)
          : '';
        const endStr = dateEnd
          ? formatDateForAPI(dateEnd)
          : dateStart
          ? formatDateForAPI(dateStart)
          : '';

        filterData.create_date_range = {
          start: startStr,
          end: endStr,
        };
      }

      onChange(filterData);
    }
  };

  const handleActNumberChange = (value: string | number) => {
    const newNumber = String(value || '');
    setActNumber(newNumber);
    updateParentData(newNumber, selectedStatuses, actDateStart, actDateEnd);
  };

  const handleStatusesChange = (values: SelectedItem[]) => {
    setSelectedStatuses(values);
    updateParentData(actNumber, values, actDateStart, actDateEnd);
  };

  const handleDateChange = (values: any) => {
    let newStart: Date | null = null;
    let newEnd: Date | null = null;

    if (Array.isArray(values)) {
      const [start, end] = values || [null, null];
      newStart = start || null;
      newEnd = end || null;
      setActDateStart(newStart);
      setActDateEnd(newEnd);
    } else if (values) {
      newStart = values;
      newEnd = values;
      setActDateStart(newStart);
      setActDateEnd(newEnd);
    } else {
      setActDateStart(null);
      setActDateEnd(null);
    }

    updateParentData(actNumber, selectedStatuses, newStart, newEnd);
  };

  const formatDateForAPI = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return (
    <TabletFilterContainer className={styles.date}>
      <TabletFilterSection>
        <TabletFilterField
          type='number'
          label='№ акта'
          value={actNumber}
          onChange={handleActNumberChange}
          placeholder='Номер акта'
          min={1}
          max={100000}
        />
      </TabletFilterSection>

      <TabletFilterSection>
        <TabletFilterField
          type='select'
          label='Статус'
          options={statusOptions}
          selectedValues={selectedStatuses}
          onChange={handleStatusesChange}
          showSearch={false}
        />
      </TabletFilterSection>

      <TabletFilterSection>
        <TabletFilterField
          type='dateRange'
          label='Дата акта'
          value={[actDateStart, actDateEnd]}
          onChange={handleDateChange}
          placeholder='Дата акта'
        />
      </TabletFilterSection>
    </TabletFilterContainer>
  );
};
